import { FormEvent, useEffect, useState } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { GlassCard } from "@/components/common/GlassCard";
import { TermsAndConditionsModal } from "@/components/common/TermsAndConditionsModal";
import { AuthPageShell } from "@/components/layout/AuthPageShell";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { EmailNotVerifiedError, useAuth } from "@/contexts/AuthContext";
import { authClient } from "@/lib/auth-client";
import { needsTermsAcceptance } from "@/lib/terms-and-conditions";

const RESEND_COOLDOWN_SECONDS = 60;

export function LoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { signIn, resendVerificationEmail, user, syncSession, acceptTerms, signOut } = useAuth();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [needsVerification, setNeedsVerification] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [resendMessage, setResendMessage] = useState<string | null>(null);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [isTermsModalOpen, setIsTermsModalOpen] = useState(false);
  const [isAcceptingTerms, setIsAcceptingTerms] = useState(false);

  useEffect(() => {
    void syncSession();
  }, [syncSession]);

  useEffect(() => {
    const error = searchParams.get("error");
    if (error === "invalid_token") {
      setNeedsVerification(true);
      setErrorMessage("This confirmation link has expired. Resend a new one below.");
    }
  }, [searchParams]);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = window.setInterval(() => {
      setResendCooldown(prev => (prev <= 1 ? 0 : prev - 1));
    }, 1000);
    return () => window.clearInterval(timer);
  }, [resendCooldown]);

  useEffect(() => {
    if (user && needsTermsAcceptance(user)) {
      setIsTermsModalOpen(true);
    }
  }, [user]);

  const redirectTo = searchParams.get("redirect");
  const safeRedirect =
    redirectTo && redirectTo.startsWith("/") && !redirectTo.startsWith("//") ? redirectTo : "/";

  if (user && !needsTermsAcceptance(user) && !isTermsModalOpen) {
    return <Navigate to={safeRedirect} replace />;
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setErrorMessage(null);
    setResendMessage(null);
    setNeedsVerification(false);
    setIsSubmitting(true);

    try {
      await signIn(email, password);
      const { data } = await authClient.getSession();
      const sessionUser = data?.user as
        | { termsAcceptedAt?: Date | string | null; termsDocumentVersion?: string | null }
        | undefined;
      if (needsTermsAcceptance(sessionUser)) {
        setIsTermsModalOpen(true);
        return;
      }
      navigate(safeRedirect, { replace: true });
    } catch (error) {
      if (error instanceof EmailNotVerifiedError) {
        setNeedsVerification(true);
        setErrorMessage(error.message);
      } else {
        const message = error instanceof Error ? error.message : "Login failed";
        setErrorMessage(message);
      }
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleAcceptTerms() {
    setErrorMessage(null);
    setIsAcceptingTerms(true);
    setIsTermsModalOpen(false);

    try {
      await acceptTerms();
      navigate(safeRedirect, { replace: true });
    } catch (error) {
      const message = error instanceof Error ? error.message : "Failed to save terms acceptance";
      setErrorMessage(message);
      setIsTermsModalOpen(true);
    } finally {
      setIsAcceptingTerms(false);
    }
  }

  async function handleDeclineTerms() {
    setIsTermsModalOpen(false);
    setErrorMessage("You must accept the Terms and Conditions to use Doculegis.");
    await signOut();
  }

  async function handleResendConfirmation() {
    if (!email.trim()) {
      setResendMessage("Enter your email address above, then try again.");
      return;
    }

    setResendMessage(null);
    setIsResending(true);

    try {
      await resendVerificationEmail(email.trim());
      setResendCooldown(RESEND_COOLDOWN_SECONDS);
      setResendMessage("Confirmation email sent. Check your inbox and spam folder.");
    } catch (error) {
      const message = error instanceof Error ? error.message : "Failed to resend email";
      setResendMessage(message);
    } finally {
      setIsResending(false);
    }
  }

  return (
    <>
      <AuthPageShell showBeta>
        <GlassCard className="border-indigo-300/25 bg-slate-950/45 p-7 shadow-[0_25px_80px_-45px_rgba(79,70,229,0.75)]">
          <div className="mb-6 text-center">
            <h1 className="text-xl font-semibold text-white">Welcome back</h1>
            <p className="mt-1 text-xs text-[var(--lm-muted)] dark:text-slate-300/85">
              Sign in to continue
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-slate-200" htmlFor="email">
                Email
              </label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={event => setEmail(event.target.value)}
                placeholder="you@company.com"
                required
                autoComplete="email"
              />
            </div>
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-slate-200" htmlFor="password">
                  Password
                </label>
                <Link
                  to="/forgot-password"
                  className="text-xs font-medium text-violet-300 transition-colors hover:text-violet-200"
                >
                  Forgot password?
                </Link>
              </div>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={event => setPassword(event.target.value)}
                placeholder="••••••••"
                required
                autoComplete="current-password"
              />
            </div>

            {errorMessage ? (
              <div className="space-y-3 rounded-lg border border-red-500/30 bg-[var(--tint-red-bg)] px-3 py-2 text-sm text-[var(--tint-red-fg)] dark:bg-red-500/15 dark:text-red-300">
                <p>{errorMessage}</p>
                {needsVerification ? (
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full border-violet-400/40 bg-violet-500/10 text-violet-100 hover:bg-violet-500/20 hover:text-white"
                    disabled={isResending || resendCooldown > 0 || !email.trim()}
                    onClick={() => void handleResendConfirmation()}
                  >
                    {isResending
                      ? "Sending…"
                      : resendCooldown > 0
                        ? `Resend available in ${resendCooldown}s`
                        : "Resend confirmation email"}
                  </Button>
                ) : null}
              </div>
            ) : null}

            {resendMessage ? (
              <p className="rounded-lg border border-violet-400/25 bg-violet-500/10 px-3 py-2 text-sm text-violet-200">
                {resendMessage}
              </p>
            ) : null}

            <Button type="submit" className="w-full" disabled={isSubmitting || isAcceptingTerms}>
              {isSubmitting ? "Signing in..." : "Sign in"}
            </Button>
          </form>
        </GlassCard>
      </AuthPageShell>

      <TermsAndConditionsModal
        isOpen={isTermsModalOpen}
        onAccept={() => void handleAcceptTerms()}
        onDecline={() => void handleDeclineTerms()}
      />
    </>
  );
}
